import { useEffect } from 'react';
import type { FillExtrusionLayerSpecification, StyleSpecification } from 'maplibre-gl';
import { useMap } from '../ui/map';

const CUSTOM_BUILDING_LAYER_ID = 'moorly-dark-basemap-building-3d';

type StyleLayerLike = {
  id: string;
  type: string;
  source?: string;
  ['source-layer']?: string;
};

function hasNativeBuildingExtrusions(layers: StyleLayerLike[] = []): boolean {
  return layers.some(
    (layer) =>
      layer.type === 'fill-extrusion'
      && typeof layer.id === 'string'
      && layer.id !== CUSTOM_BUILDING_LAYER_ID
      && layer['source-layer'] === 'building',
  );
}

function getBuildingSourceLayer(layers: StyleLayerLike[] = []): { source: string; sourceLayer: string } | null {
  for (const layer of layers) {
    const sourceLayer = layer['source-layer'];
    const source = layer.source;
    if (sourceLayer === 'building' && typeof source === 'string') {
      return { source, sourceLayer };
    }
  }
  return null;
}

function getFirstSymbolLayerId(layers: StyleLayerLike[] = []): string | undefined {
  return layers.find((layer) => layer.type === 'symbol')?.id;
}

function buildCustomLayer(source: string, sourceLayer: string): FillExtrusionLayerSpecification {
  return {
    id: CUSTOM_BUILDING_LAYER_ID,
    type: 'fill-extrusion',
    source,
    'source-layer': sourceLayer,
    minzoom: 14.5,
    filter: ['!has', 'hide_3d'],
    paint: {
      // MapTiler vector tiles expose render_* height fields on building features.
      'fill-extrusion-base': ['coalesce', ['to-number', ['get', 'render_min_height']], 0],
      'fill-extrusion-height': ['coalesce', ['to-number', ['get', 'render_height']], 0],
      'fill-extrusion-color': 'hsl(0,0%,24%)',
      'fill-extrusion-opacity': 0.52,
    },
  };
}

function ensureCustomBuildingLayer(map: { getStyle: () => StyleSpecification; getLayer: (id: string) => unknown; addLayer: (layer: FillExtrusionLayerSpecification, beforeId?: string) => void; removeLayer: (id: string) => void; }, enabled: boolean) {
  const style = map.getStyle();
  const layers = (style.layers || []) as unknown as StyleLayerLike[];
  const hasCustom = Boolean(map.getLayer(CUSTOM_BUILDING_LAYER_ID));
  const hasNative3D = hasNativeBuildingExtrusions(layers);

  if (!enabled || hasNative3D) {
    if (hasCustom) map.removeLayer(CUSTOM_BUILDING_LAYER_ID);
    return;
  }

  if (hasCustom) return;
  const buildingSource = getBuildingSourceLayer(layers);
  if (!buildingSource) return;

  const beforeId = getFirstSymbolLayerId(layers);
  map.addLayer(buildCustomLayer(buildingSource.source, buildingSource.sourceLayer), beforeId);
}

export function Basemap3DBuildingsLayer({ enabled }: { enabled: boolean }) {
  const { map, isLoaded } = useMap();

  useEffect(() => {
    if (!map || !isLoaded) return;

    const syncLayer = () => {
      try {
        ensureCustomBuildingLayer(map as any, enabled);
      } catch (err) {
        console.error('[Basemap3DBuildingsLayer] failed to sync layer', err);
      }
    };

    syncLayer();
    map.on('styledata', syncLayer);

    return () => {
      map.off('styledata', syncLayer);
      try {
        if (map.getLayer(CUSTOM_BUILDING_LAYER_ID)) {
          map.removeLayer(CUSTOM_BUILDING_LAYER_ID);
        }
      } catch {
        // Map may already be destroyed.
      }
    };
  }, [map, isLoaded, enabled]);

  return null;
}

export {
  CUSTOM_BUILDING_LAYER_ID,
  hasNativeBuildingExtrusions,
  getBuildingSourceLayer,
  getFirstSymbolLayerId,
  buildCustomLayer,
  ensureCustomBuildingLayer,
};
